import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Download, Loader2, RefreshCw, Maximize2, Lock, Unlock } from 'lucide-react';
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';

const ImageConverter: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [outputFormat, setOutputFormat] = useState('png');
  const [quality, setQuality] = useState(95);
  const [resizeWidth, setResizeWidth] = useState<number | ''>('');
  const [resizeHeight, setResizeHeight] = useState<number | ''>('');
  const [maintainAspect, setMaintainAspect] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{width: number, height: number} | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setProcessedImage(null);
      setError(null);
      
      // Get original dimensions
      const img = new Image();
      img.onload = () => {
        setOriginalDimensions({ width: img.width, height: img.height });
        setResizeWidth(img.width);
        setResizeHeight(img.height);
      };
      img.src = URL.createObjectURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.bmp', '.tiff']
    },
    multiple: false
  });

  const updateWidth = (width: number) => {
    setResizeWidth(width);
    if (maintainAspect && originalDimensions) {
      const aspectRatio = originalDimensions.height / originalDimensions.width;
      setResizeHeight(Math.round(width * aspectRatio));
    }
  };

  const updateHeight = (height: number) => {
    setResizeHeight(height);
    if (maintainAspect && originalDimensions) {
      const aspectRatio = originalDimensions.width / originalDimensions.height;
      setResizeWidth(Math.round(height * aspectRatio));
    }
  };

  const resetDimensions = () => {
    if (originalDimensions) {
      setResizeWidth(originalDimensions.width);
      setResizeHeight(originalDimensions.height);
    }
  };

  const convertImage = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('output_format', outputFormat);
      formData.append('quality', quality.toString());
      formData.append('maintain_aspect', maintainAspect.toString());
      
      if (resizeWidth && resizeWidth !== originalDimensions?.width) {
        formData.append('resize_width', resizeWidth.toString());
      }
      if (resizeHeight && resizeHeight !== originalDimensions?.height) {
        formData.append('resize_height', resizeHeight.toString());
      }

      const response = await axios.post(
        getApiUrl(API_CONFIG.ENDPOINTS.CONVERT_IMAGE),
        formData,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const url = URL.createObjectURL(response.data);
      setProcessedImage(url);
    } catch (err: any) {
      console.error('Conversion error:', err);
      setError(err.response?.data?.detail || 'Failed to convert image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadImage = () => {
    if (!processedImage || !selectedFile) return;

    const link = document.createElement('a');
    link.href = processedImage;
    const extension = outputFormat === 'jpg' ? 'jpg' : outputFormat;
    link.download = `${selectedFile.name.split('.')[0]}_converted.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const needsConversion = () => {
    if (!selectedFile || !originalDimensions) return false;
    
    const formatChanged = outputFormat !== selectedFile.type.split('/')[1];
    const sizeChanged = resizeWidth !== originalDimensions.width || resizeHeight !== originalDimensions.height;
    
    return formatChanged || sizeChanged;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image Format Conversion</h2>
        <p className="text-gray-600">
          Convert image formats and resize your images
        </p>
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        {isDragActive ? (
          <p className="text-primary-600 font-medium">Drop the image here...</p>
        ) : (
          <div>
            <p className="text-gray-600 font-medium mb-2">
              Drag & drop an image here, or click to select
            </p>
            <p className="text-sm text-gray-500">
              Supports PNG, JPG, JPEG, WEBP, BMP, TIFF
            </p>
          </div>
        )}
      </div>

      {/* Selected Image Info */}
      {selectedFile && originalDimensions && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Image</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="aspect-video bg-white rounded-lg overflow-hidden">
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Original"
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Filename:</span>
                <p className="text-sm text-gray-600">{selectedFile.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Format:</span>
                <p className="text-sm text-gray-600 uppercase">{selectedFile.type.split('/')[1]}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Size:</span>
                <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Dimensions:</span>
                <p className="text-sm text-gray-600">
                  {originalDimensions.width} × {originalDimensions.height} pixels
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conversion Settings */}
      {selectedFile && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Conversion Settings</h3>

          <div className="space-y-6">
            {/* Output Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Format
              </label>
              <select
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="png">PNG (Lossless, supports transparency)</option>
                <option value="jpg">JPG (Smaller file size)</option>
                <option value="webp">WebP (Modern format, good compression)</option>
                <option value="bmp">BMP (Uncompressed)</option>
                <option value="tiff">TIFF (High quality)</option>
              </select>
            </div>

            {/* Quality Setting */}
            {(outputFormat === 'jpg' || outputFormat === 'webp') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality ({quality}%)
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={quality}
                  onChange={(e) => setQuality(parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Lower quality</span>
                  <span>Higher quality</span>
                </div>
              </div>
            )}

            {/* Resize Settings */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">
                  Resize Image
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setMaintainAspect(!maintainAspect)}
                    className={`p-1 rounded ${
                      maintainAspect ? 'text-primary-600' : 'text-gray-400'
                    }`}
                    title={maintainAspect ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}
                  >
                    {maintainAspect ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={resetDimensions}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="Reset to original dimensions"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Width (px)
                  </label>
                  <input
                    type="number"
                    value={resizeWidth}
                    onChange={(e) => updateWidth(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Height (px)
                  </label>
                  <input
                    type="number"
                    value={resizeHeight}
                    onChange={(e) => updateHeight(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="maintain-aspect"
                  checked={maintainAspect}
                  onChange={(e) => setMaintainAspect(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="maintain-aspect" className="ml-2 text-sm text-gray-600">
                  Maintain aspect ratio
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Convert Button */}
      {selectedFile && (
        <div className="text-center">
          <button
            onClick={convertImage}
            disabled={isProcessing || !needsConversion()}
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Converting...
              </>
            ) : (
              <>
                <Maximize2 className="w-5 h-5 mr-2" />
                Convert Image
              </>
            )}
          </button>
          {!needsConversion() && (
            <p className="text-sm text-gray-500 mt-2">
              Change format or dimensions to convert the image
            </p>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Results */}
      {processedImage && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Converted Image</h3>
            <button
              onClick={downloadImage}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-5 h-5 mr-2" />
              Download
            </button>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="aspect-video bg-white rounded-lg overflow-hidden">
              <img
                src={processedImage}
                alt="Converted"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageConverter;